import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const HowItWorksPage = () => {
  const steps = [
    {
      number: '01',
      title: 'Create, Sign, and Fund Petitions',
      description: 'Anyone can create a petition, sign one, or fund one—all directly on the blockchain. When you publish a petition, it becomes visible for others to support. People can sign your petition to show collective support or contribute ETH to help fund its cause. Every vote, signature, and contribution is recorded transparently by the Petition Core smart contract on the blockchain.',
      badge: 'Fully on-chain',
      image: '/images/create-sign-fund.jpg',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      number: '02',
      title: 'Leave or Edit a Petition',
      description: 'If you decide to withdraw or modify your petition, you can do so directly through the Petition Core dashboard. All edits and actions are recorded transparently on-chain, maintaining a complete history.',
      badge: 'Full transparency',
      image: '/images/Leave-Edit-Petition.jpg',
      gradient: 'from-cyan-500 to-teal-500'
    },
    {
      number: '03',
      title: 'Campaign Ending & Payments',
      description: 'When a petition reaches its funding goal or deadline, the Petition Core contract automatically finalizes it. The campaign locks for new contributions, records the final total, and marks the petition as completed. The collected ETH is then sent directly to the creator\'s wallet and contributors. Total raised, number of signers, and transaction hash can be publicly viewed on the blockchain for full transparency, with no middleman or centralized control.',
      badge: 'Automated smart contract',
      image: '/images/Campaign-Ending-Payments.jpg',
      gradient: 'from-teal-500 to-green-500'
    },
    {
      number: '04',
      title: 'Download Petition PDF',
      description: 'Every petition you create or support generates a verifiable PDF. It includes the petition title, description, and blockchain transaction hash for proof of authenticity. This feature demonstrates how decentralized petitions can be publicly verified and preserved—a foundation for future legal and social use cases.',
      badge: 'Blockchain-verified proof',
      image: '/images/download-petition-pdf.jpg',
      gradient: 'from-green-500 to-emerald-500'
    }
  ];

  return (
    <>
      <Helmet>
        <title>How It Works - Petition.io | Blockchain Petition Platform</title>
        <meta name="description" content="Learn how to create, sign, and fund decentralized petitions on the Ethereum blockchain. Simple, transparent, and powerful." />
        <meta property="og:title" content="How Petition.io Works - Decentralized Petitions" />
        <meta property="og:description" content="Step-by-step guide to creating and supporting blockchain-based petitions" />
        <link rel="canonical" href="https://petition.io/how-it-works" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 py-12 sm:py-16 md:py-20 pt-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              className="inline-block mb-6"
            >
            </motion.div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-neutral-dark mb-6 font-lato">
              How It Works
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-montserrat">
              Simple, transparent, and powerful
            </p>
          </motion.div>

          <div className="space-y-12 sm:space-y-16 md:space-y-20">
            {steps.map((step, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: idx % 2 === 0 ? -50 : 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: idx * 0.2 }}
                className={`flex flex-col ${idx % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-8 sm:gap-10 lg:gap-12`}
              >
                {/* Content */}
                <div className="flex-1 px-4">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    className={`inline-block text-4xl sm:text-5xl md:text-6xl font-bold bg-gradient-to-br ${step.gradient} bg-clip-text text-transparent mb-3 sm:mb-4`}
                  >
                    {step.number}
                  </motion.div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-neutral-dark mb-3 sm:mb-4">{step.title}</h2>
                  <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-4 sm:mb-6">{step.description}</p>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    className="inline-flex items-center px-3 sm:px-4 py-2 bg-primary-blue/10 border border-primary-blue/20 rounded-full"
                  >
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-2 text-primary-blue" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-xs sm:text-sm font-medium text-primary-blue">{step.badge}</span>
                  </motion.div>
                </div>

                {/* Image */}
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  transition={{ duration: 0.3 }}
                  className="flex-1 relative w-full"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${step.gradient} opacity-20 rounded-2xl sm:rounded-3xl blur-2xl`}></div>
                  <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl shadow-2xl">
                    <img 
                      src={step.image} 
                      alt={step.title}
                      className="w-full h-64 sm:h-72 md:h-80 object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                    <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 right-4 sm:right-6">
                      <div className="bg-white/90 backdrop-blur-sm rounded-lg sm:rounded-xl px-3 sm:px-4 py-2 sm:py-3">
                        <p className="text-xs sm:text-sm font-semibold text-neutral-dark">Step {step.number}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
            className="mt-12 sm:mt-16 md:mt-20 text-center"
          >
            <Link to="/why-join">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-primary-blue to-primary-light text-white text-base sm:text-lg font-semibold rounded-xl shadow-lg inline-flex items-center"
              >
                <span className="text-sm sm:text-base md:text-lg">Why Join Petition.io</span>
                <svg className="ml-2 w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default HowItWorksPage;
